'use client';

import { usePathname } from '@/i18n/navigation';
import { Alibaba, Google, Grok, NanoBanana, OpenAI, Qwen, Runway, Sora, XAI } from '@lobehub/icons';
import Image from 'next/image';
import Link from 'next/link';

export default function Footer({ isInjectable = false }: { isInjectable?: boolean }) {
  const pathname = usePathname();
  const isSoraPage =
    pathname?.includes('sora-2-ai-video-generator') ||
    pathname?.includes('nano-banana-2-ai-image-generator') ||
    pathname?.includes('z-image-ai-image-generator') ||
    pathname?.includes('runway-gen-4-ai-video-generator') ||
    pathname?.includes('grok-imagine-ai-video-generator') ||
    pathname?.includes('grok-imagine-ai-video-upscaler') ||
    pathname?.includes('/contact') ||
    pathname?.includes('/help') ||
    pathname?.includes('/pricing') ||
    pathname?.includes('/how-it-works');

  const linkTarget = isInjectable ? '_parent' : undefined;

  return (
    <footer className="footer">
      <div className="container">
        <div
          className={`footer-content ${isSoraPage ? '!flex !flex-row !flex-wrap !justify-center !items-start !gap-12 md:!gap-24' : ''}`}
        >
          <div className={`footer-section ${isSoraPage ? 'w-full md:w-auto' : ''}`}>
            <div className="logo">
              <Image
                src="/images/zappingai-logo-small.png"
                alt="ZappingAI"
                width={111}
                height={40}
                className="logo-img h-auto w-auto"
                priority
                sizes="(max-width: 640px) 80px, (max-width: 1024px) 120px, 150px"
              />
            </div>
            <p>Zapping Complexity, Masters of AI.</p>
            {isSoraPage && (
              <div className="mt-4 flex items-center justify-center gap-4 opacity-90 transition-opacity hover:opacity-100 md:justify-start">
                <Image
                  src="/images/apple-pay.svg"
                  alt="Apple Pay"
                  title="Pay with Apple Pay"
                  width={44}
                  height={28}
                  className="h-7 w-auto"
                />
                <Image
                  src="/images/google-pay.svg"
                  alt="Google Pay"
                  title="Pay with Google Pay"
                  width={44}
                  height={28}
                  className="h-7 w-auto"
                />
                <Image
                  src="/images/visa.svg"
                  alt="Visa"
                  title="Pay with Visa"
                  width={44}
                  height={28}
                  className="h-7 w-auto"
                />
                <Image
                  src="/images/mastercard.svg"
                  alt="Mastercard"
                  title="Pay with Mastercard"
                  width={44}
                  height={28}
                  className="h-7 w-auto"
                />
                <Image
                  src="/images/paypal.svg"
                  alt="PayPal"
                  title="Pay with PayPal"
                  width={44}
                  height={28}
                  className="h-7 w-auto"
                />
              </div>
            )}
            {isSoraPage && (
              <>
                {/* Companies Row */}
                <div className="mt-4 flex items-center justify-center gap-4 md:justify-start">
                  <div title="Google">
                    <Google.Avatar size={34} shape="square" style={{ borderRadius: '6px' }} />
                  </div>
                  <div title="xAI">
                    <XAI.Avatar size={34} shape="square" style={{ borderRadius: '6px' }} />
                  </div>
                  <div title="Runway">
                    <div
                      style={{
                        width: '34px',
                        height: '34px',
                        backgroundColor: 'black',
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Runway size={22} style={{ color: 'white' }} />
                    </div>
                  </div>
                  <div title="OpenAI">
                    <OpenAI.Avatar size={34} shape="square" style={{ borderRadius: '6px' }} />
                  </div>
                  <div title="Alibaba">
                    <Alibaba.Avatar size={34} shape="square" style={{ borderRadius: '6px' }} />
                  </div>
                </div>

                {/* Models Row */}
                <div className="mt-4 flex items-center justify-center gap-4 md:justify-start">
                  <div title="Nano Banana 2">
                    <NanoBanana.Avatar size={34} shape="square" style={{ borderRadius: '6px' }} />
                  </div>
                  <div title="Grok Imagine">
                    <Grok.Avatar size={34} shape="square" style={{ borderRadius: '6px' }} />
                  </div>
                  <div title="Runway Gen 4">
                    <Runway.Avatar size={34} shape="square" style={{ borderRadius: '6px' }} />
                  </div>
                  <div title="Sora 2">
                    <Sora.Avatar size={34} shape="square" style={{ borderRadius: '6px' }} />
                  </div>
                  <div title="Z-Image">
                    <Qwen.Avatar size={34} shape="square" style={{ borderRadius: '6px' }} />
                  </div>
                </div>
              </>
            )}
          </div>
          {/* Products and Company sections - Hidden on Sora Generator page and pre-sale pages */}
          {!isSoraPage && (
            <>
              <div className="footer-section">
                <h2>Products</h2>
                <ul>
                  <li>
                    <Link href="/#automate" target={linkTarget}>
                      ZapFlow
                    </Link>
                  </li>
                  <li>
                    <Link href="/sora-2-ai-video-generator" target={linkTarget}>
                      Sora 2
                    </Link>
                  </li>
                  <li>
                    <Link href="/#communicate" target={linkTarget}>
                      ZapChat
                    </Link>
                  </li>
                  <li>
                    <Link href="/#communicate" target={linkTarget}>
                      ZapVoice
                    </Link>
                  </li>
                </ul>
              </div>
              <div className="footer-section">
                <h2>Company</h2>
                <ul>
                  <li>
                    <Link href="/#communicate" target={linkTarget}>
                      Products
                    </Link>
                  </li>
                  <li>
                    <Link href="/#automate" target={linkTarget}>
                      Solutions
                    </Link>
                  </li>
                  <li>
                    <Link href="/#about" target={linkTarget}>
                      About Us
                    </Link>
                  </li>
                  <li>
                    <Link href="/#contact" target={linkTarget}>
                      Consultation
                    </Link>
                  </li>
                </ul>
              </div>
            </>
          )}
          {/* Help section - Shown only on Sora Generator page and pre-sale pages */}
          {isSoraPage && (
            <div className="footer-section">
              <h2>Help</h2>
              <ul>
                <li>
                  <Link href="/how-it-works" target={linkTarget}>
                    How It Works
                  </Link>
                </li>
                <li>
                  <Link href="/pricing" target={linkTarget}>
                    Pricing
                  </Link>
                </li>
                <li>
                  <Link href="/help" target={linkTarget}>
                    FAQ
                  </Link>
                </li>
                <li>
                  <Link href="/contact" target={linkTarget}>
                    Contact Us
                  </Link>
                </li>
              </ul>
            </div>
          )}
          <div className="footer-section">
            <h2>Legal</h2>
            <ul>
              <li>
                <Link href="/privacy" target={linkTarget}>
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link href="/terms" target={linkTarget}>
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link href="/refund" target={linkTarget}>
                  Refund Policy
                </Link>
              </li>
              <li>
                <Link href="/subscription" target={linkTarget}>
                  Subscription Terms
                </Link>
              </li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy;{new Date().getFullYear()} ZappingAI.com | All rights reserved.</p>
          <div className="social-links">
            <a
              target="_blank"
              href="https://x.com/ZappingAI"
              rel="noreferrer noopener"
              aria-label="Twitter"
            >
              <i className="fa-brands fa-x-twitter" />
            </a>
            <a
              target="_blank"
              href="https://www.linkedin.com/company/ZappingAI/"
              rel="noreferrer noopener"
              aria-label="LinkedIn"
            >
              <i className="fab fa-linkedin" />
            </a>
            <a
              target="_blank"
              href="https://www.facebook.com/ZappingAI"
              rel="noreferrer noopener"
              aria-label="Facebook"
            >
              <i className="fab fa-facebook" />
            </a>
            <a
              target="_blank"
              href="https://www.instagram.com/zapping.ai/"
              rel="noreferrer noopener"
              aria-label="Instagram"
            >
              <i className="fab fa-instagram" />
            </a>
            <a
              target="_blank"
              href="https://www.tiktok.com/@ZappingAI"
              rel="noreferrer noopener"
              aria-label="TikTok"
            >
              <i className="fab fa-tiktok" />
            </a>
            <a
              target="_blank"
              href="https://www.youtube.com/@ZappingAI"
              rel="noreferrer noopener"
              aria-label="YouTube"
            >
              <i className="fab fa-youtube" />
            </a>
            <a
              href="https://up.zappingai.com/status/checker"
              target="_blank"
              rel="noreferrer noopener"
              aria-label="UPTIME: 100%"
              style={{ cursor: 'pointer', width: 'auto' }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="96"
                height="20"
                role="img"
                aria-label="UPTIME: 100%"
              >
                <title>UPTIME: 100%</title>
                <linearGradient id="s" x2="0" y2="100%">
                  <stop offset="0" stopColor="#bbb" stopOpacity=".1" />
                  <stop offset="1" stopOpacity=".1" />
                </linearGradient>
                <clipPath id="r">
                  <rect width="96" height="20" rx="3" fill="#fff" />
                </clipPath>
                <g clipPath="url(#r)">
                  <rect width="53" height="20" fill="#555" />
                  <rect x="53" width="43" height="20" fill="#4c1" />
                  <rect width="96" height="20" fill="url(#s)" />
                </g>
                <g
                  fill="#fff"
                  textAnchor="middle"
                  fontFamily="Verdana,Geneva,DejaVu Sans,sans-serif"
                  textRendering="geometricPrecision"
                  fontSize="110"
                >
                  <text
                    x="275"
                    y="150"
                    fill="#010101"
                    fillOpacity=".3"
                    transform="scale(.1)"
                    textLength="430"
                  >
                    UPTIME
                  </text>
                  <text x="275" y="140" transform="scale(.1)" fill="#fff" textLength="430">
                    UPTIME
                  </text>
                  <text
                    x="735"
                    y="150"
                    fill="#010101"
                    fillOpacity=".3"
                    transform="scale(.1)"
                    textLength="330"
                  >
                    100%
                  </text>
                  <text x="735" y="140" transform="scale(.1)" fill="#fff" textLength="330">
                    100%
                  </text>
                </g>
              </svg>
            </a>
          </div>
        </div>
      </div>
      {/* Metricool Tracker - Lazy Loaded */}
      <Image
        src="https://tracker.metricool.com/c3po.jpg?hash=e72e55f272fbdaf74170bd5ad8a92834"
        width={1}
        height={1}
        style={{ display: 'none' }}
        unoptimized
        alt=""
        aria-hidden="true"
      />
    </footer>
  );
}
